
/**
 * DocumentRevision Service Layer
 * Rule: Business logic, validation, and API orchestration
 * Rule: NO UI IN SDK
 */
import { ApiError } from '../errors/api-error.js';
import { withRetry } from '../utils/retry.js';

export class DocumentRevisionService {
    constructor(api, config = {}) {
        this.api = api;
        this.config = config;
    }

    /**
     * Get all revisions for a document
     */
    async getRevisions(docId, params = {}) {
        try {
            const response = await withRetry(
                () => this.api.getRevisions(docId, params),
                { maxRetries: 2 }
            );
            return {
                success: true,
                data: response.data,
                meta: response.meta || {},
                message: response.message || 'Revizyonlar getirildi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch revisions');
        }
    }

    /**
     * Get active revision
     */
    async getActiveRevision(docId) {
        try {
            const response = await withRetry(
                () => this.api.getActiveRevision(docId),
                { maxRetries: 2 }
            );
            return {
                success: true,
                data: response.data,
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch active revision');
        }
    }

    /**
     * Get revision by ID
     */
    async getRevisionById(docId, revId) {
        try {
            const response = await withRetry(
                () => this.api.getRevisionById(docId, revId),
                { maxRetries: 2 }
            );
            return {
                success: true,
                data: response.data,
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch revision');
        }
    }

    /**
     * Create new revision (file upload)
     * @param {Function|{ onUploadProgress?: Function }} [optionsOrCallback]
     */
    async createRevision(docId, formData, optionsOrCallback) {
        try {
            const response = await this.api.createRevision(docId, formData, optionsOrCallback);
            return {
                success: true,
                data: response.data,
                message: response.message || 'Revizyon oluşturuldu'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to create revision');
        }
    }

    /**
     * Activate a revision
     */
    async activateRevision(docId, revId) {
        try {
            const response = await this.api.activateRevision(docId, revId);
            return {
                success: true,
                data: response.data,
                message: response.message || 'Revizyon aktif edildi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to activate revision');
        }
    }

    /**
     * Download revision file
     */
    async downloadRevision(docId, revId) {
        try {
            const response = await this.api.downloadRevision(docId, revId);
            return response; // Returns blob directly
        } catch (error) {
            throw this._handleError(error, 'Failed to download revision');
        }
    }

    async exportRevisionWithMetadata(docId, revId, payload) {
        try {
            return await this.api.exportRevisionWithMetadata(docId, revId, payload);
        } catch (error) {
            throw this._handleError(error, 'Failed to export document');
        }
    }

    /**
     * Compare two revisions (Word only)
     * @param {Record<string, string|boolean|number|undefined>} [compareQuery]
     */
    async compareRevisions(docId, revId, targetRevId, compareQuery = {}) {
        try {
            const response = await this.api.compareRevisions(docId, revId, targetRevId, compareQuery);
            return response; // Returns blob directly
        } catch (error) {
            throw this._handleError(error, 'Failed to compare revisions');
        }
    }

    async getRevisionPdfBlob(docId, revId, query = {}) {
        try {
            return await this.api.getRevisionPdfBlob(docId, revId, query);
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch revision PDF');
        }
    }

    async getCompareResultPdfBlob(docId, baseRevId, targetRevId, query = {}) {
        try {
            return await this.api.getCompareResultPdfBlob(docId, baseRevId, targetRevId, query);
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch compare result PDF');
        }
    }

    async persistRevisionPdf(docId, revId, query = {}) {
        try {
            return await this.api.persistRevisionPdf(docId, revId, query);
        } catch (error) {
            throw this._handleError(error, 'Failed to persist revision PDF');
        }
    }

    async applyTrackChanges(docId, revId, enabled, trackQuery = {}) {
        try {
            return await this.api.applyTrackChanges(docId, revId, enabled, trackQuery);
        } catch (error) {
            throw this._handleError(error, 'Failed to apply track changes');
        }
    }

    /**
     * Submit revision for approval
     */
    async submitForApproval(docId, revId) {
        try {
            const response = await this.api.submitForApproval(docId, revId);
            return {
                success: true,
                data: response.data,
                message: response.message || 'Revizyon onaya gönderildi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to submit for approval');
        }
    }

    /**
     * Approve revision
     */
    async approveRevision(docId, revId, data = {}) {
        try {
            const response = await this.api.approveRevision(docId, revId, data);
            return {
                success: true,
                data: response.data,
                message: response.message || 'Revizyon onaylandı'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to approve revision');
        }
    }

    /**
     * Reject revision
     */
    async rejectRevision(docId, revId, data = {}) {
        try {
            const response = await this.api.rejectRevision(docId, revId, data);
            return {
                success: true,
                data: response.data,
                message: response.message || 'Revizyon reddedildi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to reject revision');
        }
    }

    /**
     * Handle and transform errors
     */
    _handleError(error, defaultMessage) {
        if (error instanceof ApiError) {
            throw error;
        }

        if (error.statusCode || error.status) {
            throw ApiError.fromResponse(error);
        }

        throw new ApiError(error.message || defaultMessage, 500, 'UNKNOWN_ERROR', error);
    }
}
